import { useEffect } from 'react'

/**
 * Shared keyboard-navigation primitives for the TreeView popover menus
 * (Workspace2Popover, MoveBlockPopover, TasksSubmenu, BestPracticesSettingPopover),
 * which all share the same roving-focus pattern over MUI v4 portal content.
 */

/**
 * Computes the next index in a cyclic focus list.
 * @param currentIdx Index of the currently focused item (-1 when focus is outside the list).
 * @param itemCount Total number of focusable items.
 * @param goBack True to move backwards (ArrowUp / Shift+Tab), false forwards.
 */
export const getCycledIndex = (
    currentIdx: number,
    itemCount: number,
    goBack: boolean
): number =>
    goBack
        ? (currentIdx - 1 + itemCount) % itemCount
        : (currentIdx + 1) % itemCount

/**
 * Attaches `handler` to document keydown in capture phase while `active`
 * (capture is required because MUI Modal stops propagation at the paper),
 * removing it on deactivation/unmount.
 */
export const useCaptureKeydown = (
    active: boolean,
    handler: (e: KeyboardEvent) => void
): void => {
    useEffect(() => {
        if (!active) return undefined
        document.addEventListener('keydown', handler, true)
        return () => document.removeEventListener('keydown', handler, true)
    }, [active, handler])
}

/**
 * Runs attempt() immediately; while it keeps returning false — element not yet
 * rendered, since MUI v4 portals mount asynchronously — retries on DOM mutations
 * until it succeeds. Returns a disposer callers must invoke on cleanup.
 */
export const runWhenInDom = (attempt: () => boolean): (() => void) => {
    if (attempt()) return () => {}
    let observer: MutationObserver | null = new MutationObserver(() => {
        if (attempt()) {
            observer?.disconnect()
            observer = null
        }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    return () => {
        observer?.disconnect()
        observer = null
    }
}
